import { forwardRef } from 'react'

const variantClass = {
  // Primary CTA — diagonal navy→cyan brand gradient. The
  // `.taskmagix-btn-primary` class lives in index.css and handles the
  // background, white text, transparent border, and the
  // `hover:brightness` lift so we keep the styling co-located with
  // the other brand surfaces (sidebar / page bg).
  primary: 'taskmagix-btn-primary font-semibold',
  secondary:
    'bg-[color:var(--color-card)] text-[color:var(--color-ink)] border border-[color:var(--color-rule-strong)] hover:bg-[color:var(--color-card-hover)] hover:border-[color:var(--color-ink-muted)]',
  ghost:
    'bg-transparent text-[color:var(--color-ink)] border border-transparent hover:bg-[color:var(--color-card-hover)]',
  danger:
    'bg-[color:var(--color-priority-urgent)] text-white border border-[color:var(--color-priority-urgent)] hover:brightness-110',
}

// Padded both axes — height is no longer fixed via `h-*`, the padding
// drives the size. Gives the button room to breathe and matches the
// "View All Programs" reference button proportions.
const sizeClass = {
  sm: 'px-4 py-2',
  md: 'px-6 py-3',
}

// Shared base — `rounded-xl` (12px) matches the new "View All Programs"
// reference button. Primary variants get their own gradient shadow via
// `.taskmagix-btn-primary` in index.css; secondary/ghost/danger keep the
// subtle `shadow-sm → shadow` lift here. Focus ring uses the brand
// ocean blue so keyboard users get a soft accent halo.
// `text-sm` bumps the label one pixel above the 13px body so the
// button reads as a slightly louder CTA on every breakpoint.
const baseClass =
  'inline-flex items-center justify-center gap-2 rounded-xl font-medium text-sm ' +
  'cursor-pointer transition-all shadow-sm hover:shadow ' +
  'focus-visible:outline-none focus-visible:ring-2 ' +
  'focus-visible:ring-[color:var(--color-primary)]/25 focus-visible:ring-offset-1 ' +
  'disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none'

export const Button = forwardRef(function Button(
  {
    variant = 'primary',
    size = 'md',
    loading,
    disabled,
    className,
    children,
    ...rest
  },
  ref,
) {
  return (
    <button
      ref={ref}
      disabled={disabled || loading}
      className={`${baseClass} ${variantClass[variant]} ${sizeClass[size]} ${className ?? ''}`}
      {...rest}
    >
      {loading ? <span className="inline-block w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" /> : null}
      {children}
    </button>
  )
})
